/**
 * IncidenciasPermanentesAutonomo
 *
 * Envoltura de <IncidenciasPermanentesEmpleado /> para usarlo FUERA del Sobre Recibo
 * (pestaña de Editar Empleado y pantalla "Creditos y Movimientos").
 *
 * El componente envuelto dibuja su cuadrícula sobre los días del periodo abierto: necesita
 * `prenomina.fechaInicio` / `prenomina.fechaFin` y el catálogo `tiposIncidencias`, que dentro de la
 * prenómina le llegan del contenedor. Fuera de ahí no hay ninguno de los dos, así que este wrapper
 * los consigue por su cuenta y elige un PERIODO DE REFERENCIA.
 *
 * El periodo de referencia no es cosmético: con `tipoPatron: 'diaPeriodo'` las claves guardadas son
 * posiciones 1..N dentro del periodo, así que de cuál periodo se trate cambia lo que el usuario está
 * marcando. Por eso se muestra explícito y se puede cambiar, en vez de esconderlo.
 */

import React, { Component } from 'react'
import axios from 'axios'
import { Skeleton, Alert } from 'antd'
import { connect } from 'react-redux'
import IncidenciasPermanentesEmpleado from './IncidenciasPermanentesEmpleado'

const fmtFecha = (f) => {
  if (!f) return '—'
  const p = (f + '').split('T')[0].split('-')
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (f + '')
}

/** Sólo la parte de fecha, en 'YYYY-MM-DD'. Comparar así evita cualquier corrimiento de zona. */
const soloDia = (f) => (f ? (f + '').split('T')[0] : '')

const selectStyle = {
  padding: '3px 8px', border: '1px solid #d0d0d0', borderRadius: 4,
  fontSize: 12, background: 'white', cursor: 'pointer', height: 26,
}

class IncidenciasPermanentesAutonomo extends Component {

  state = {
    cargando:         true,
    error:            null,
    tiposIncidencias: [],
    periodos:         [],
    periodoId:        null,
  }

  componentDidMount() {
    this.cargarCatalogos()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.empresaId !== this.props.empresaId) this.cargarCatalogos()
  }

  async cargarCatalogos() {
    const { empresaId, headersToken } = this.props
    if (!empresaId) return
    this.setState({ cargando: true, error: null })
    try {
      const [resTipos, resPeriodos] = await Promise.all([
        axios.post('tiposIncidencias/selectTodasTiposIncidencias', { empresaId }, { headers: headersToken }),
        // valor 3 = periodos abiertos y cerrados; interesa el calendario, no el estatus.
        axios.post('periodos/selectTodasPeriodos', { empresaId, valor: 3 }, { headers: headersToken }),
      ])

      const tiposIncidencias = resTipos.data.success === 2 ? (resTipos.data.data || []) : []
      const periodos = resPeriodos.data.success === 2
        ? this.aplanarPeriodos(resPeriodos.data.data || [])
        : []

      this.setState({
        tiposIncidencias,
        periodos,
        periodoId: this.elegirPeriodoPorDefecto(periodos),
      })
    } catch {
      this.setState({ error: 'Error de conexión al cargar el catálogo de incidencias y los periodos' })
    }
    this.setState({ cargando: false })
  }

  /** El endpoint devuelve un árbol tipoPeriodo → ejercicio → periodos. Aquí se aplana. */
  aplanarPeriodos(arbol) {
    const lista = []
    for (const tipo of arbol) {
      for (const ejercicio of tipo.ejercicios || []) {
        for (const periodo of ejercicio.periodos || []) {
          lista.push({ ...periodo, tipoPeriodoNombre: tipo.nombre })
        }
      }
    }
    return lista.sort((a, b) => soloDia(b.fechaInicio).localeCompare(soloDia(a.fechaInicio)))
  }

  /**
   * El periodo que contiene el día de hoy; si el empleado tiene tipo de periodo, se prefiere el
   * suyo. Si hoy no cae en ninguno (calendario incompleto), el más reciente.
   */
  elegirPeriodoPorDefecto(periodos) {
    if (!periodos.length) return null

    const hoy = new Date()
    const hoyDia = [
      hoy.getFullYear(),
      String(hoy.getMonth() + 1).padStart(2, '0'),
      String(hoy.getDate()).padStart(2, '0'),
    ].join('-')

    const tipoEmpleado = this.props.tipoPeriodoId ? String(this.props.tipoPeriodoId) : null
    const delTipo = tipoEmpleado
      ? periodos.filter(p => String(p.tipoPeriodoId) === tipoEmpleado)
      : []

    const candidatos = delTipo.length ? delTipo : periodos
    const vigente = candidatos.find(p =>
      soloDia(p.fechaInicio) <= hoyDia && hoyDia <= soloDia(p.fechaFin)
    )
    return (vigente || candidatos[0])._id
  }

  renderBarraPeriodo(periodo) {
    const { periodos, periodoId } = this.state
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        background: '#fafcff', border: '1px solid #e6f0ff', borderRadius: 4,
        padding: '6px 10px', marginBottom: 10,
      }}>
        <span style={{ fontSize: 11, color: '#888' }}>Periodo de referencia:</span>
        <select
          style={selectStyle}
          value={periodoId || ''}
          onChange={e => this.setState({ periodoId: e.target.value })}
        >
          {periodos.map(p => (
            <option key={p._id} value={p._id}>
              {p.serie}-{p.numero} · {p.tipoPeriodoNombre} · {fmtFecha(p.fechaInicio)} al {fmtFecha(p.fechaFin)}
            </option>
          ))}
        </select>
        {periodo && (
          <span style={{ fontSize: 11, color: '#888' }}>
            La cuadrícula de abajo se dibuja sobre este periodo. La configuración aplica a todos los
            periodos vigentes, no sólo a éste.
          </span>
        )}
      </div>
    )
  }

  render() {
    const { cargando, error, tiposIncidencias, periodos, periodoId } = this.state
    const { empleadoId, readOnly, onCambio } = this.props

    if (cargando) return <Skeleton active paragraph={{ rows: 4 }} />
    if (error) return <div style={{ color: '#cf1322', fontSize: 12, padding: 12 }}>{error}</div>

    const periodo = periodos.find(p => p._id === periodoId) || null

    return (
      <>
        {periodos.length > 0
          ? this.renderBarraPeriodo(periodo)
          : (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 10 }}
              message="Esta empresa todavía no tiene periodos de nómina generados"
              description="Sin un periodo no se puede dibujar la cuadrícula de días. Genera los periodos en Configuración de nómina."
            />
          )}

        <IncidenciasPermanentesEmpleado
          empleadoId={empleadoId}
          prenomina={periodo ? { fechaInicio: periodo.fechaInicio, fechaFin: periodo.fechaFin } : null}
          tiposIncidencias={tiposIncidencias}
          readOnly={readOnly}
          onCambio={onCambio}
        />
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId:    state.empresa.id,
})

export default connect(mapStateToProps)(IncidenciasPermanentesAutonomo)
